import type { paths, components } from '../generated/schema';

export type SchemaTypes = components['schemas'];

export type ProcessingStatusType = 'PROCESSING' | 'COMPLETE' | 'FAILED' | 'PREPROCESSING';

export type PermissionType = 'view' | 'add' | 'delete' | 'change' | 'change_user_role' |
'create_proposal' | 'upload_resource' | 'add_user' | 'view_users' | 'remove_user' |
'add_memory_items' | 'assign_sections' | 'edit_key_value_items';

// TODO type more specifically to PermissionType[] once I figure out
// how to properly type schema with enum types
export interface ObjectWithPermissions { permissions: string[] };

const SUCCESS_CODE = 200 as const;

export type Request = 'get' | 'post' | 'delete' | 'patch';

export type Content = 'application/json' | 'multipart/form-data';

type PathWithRequestType<RequestType extends Request> = Record<RequestType, any>;

type PathsWithRequestType<RequestType extends Request> = {
  [K in keyof paths]: paths[K] extends PathWithRequestType<RequestType> ? paths[K] : never
};

export type RouteWithRequestType<RequestType extends Request> = keyof PathsWithRequestType<RequestType>;

export type ResponseDataType<RequestType extends Request, Route extends RouteWithRequestType<RequestType>> =
PathsWithRequestType<RequestType>[Route][RequestType]['responses'][typeof SUCCESS_CODE]['content']['application/json'];

export type ResponseInputType<
  RequestType extends Request,
  ContentType extends Content,
  Route extends RouteWithRequestType<RequestType>
> =
  PathsWithRequestType<RequestType>[Route][RequestType]['requestBody']['content'][ContentType];

export type QueryParamsType<
  RequestType extends Request,
  Route extends RouteWithRequestType<RequestType>
> =
  PathsWithRequestType<RequestType>[Route][RequestType]['parameters']['query'];

export type RouteWithGet = RouteWithRequestType<'get'>;
export type GetResponseDataType<Route extends RouteWithGet> = ResponseDataType<'get', Route>;
export type GetRequestQueryParamsType<Route extends RouteWithGet> = QueryParamsType<'get', Route>;
export type PaginatedRoute = '/app/users'
| '/app/opportunities'
| '/app/opportunity-files'
| '/app/proposals'
| `/app/proposals/${string}/memory-items`
| '/app/requirement-responses'
| '/app/organizations'
| `/app/organizations/${string}/memory-items`
| '/app/organization-invites'
| '/app/organization-resources';

type SchemaType<T extends keyof components['schemas']> = components['schemas'][T];

export type RequirementResponse = SchemaType<'RequirementResponseSchema'>;
export type Proposal = SchemaType<'ProposalSchema'>;
export type OrganizationInvite = SchemaType<'OrganizationInviteSchema'>;
export type OrganizationResource = SchemaType<'OrganizationResourceSchema'>;
export type OrganizationResourceTag = SchemaType<'OrganizationResourceTagSchema'>;
export type ProposalSection = SchemaType<'ProposalSectionSchema'>;
export type OpportunityRequirement = SchemaType<'OpportunityRequirementSchema'>;
export type OpportunityFile = SchemaType<'OpportunityFileSchema'>;
export type EnsisUser = SchemaType<'UserSchema'>;

export type SourceItem = SchemaType<'SourceItemSchema'>;
export type MemoryItem = SchemaType<'MemoryItemSchema'>;
export type SourceAnalysis = SchemaType<'SourceAnalysisSchema'>;
export type StorageProviderConfig = SchemaType<'StorageProviderConfigSchema'>;
export type StorageProviderSyncConfig = SchemaType<'StorageProviderSyncConfigSchema'>;

export type ConversationMessage = SchemaType<'MessageSchema'>;

export type KeyValueItem = SchemaType<'KeyValueItemSchema'>;
export type Organization = SchemaType<'OrganizationSchema'>;

export type WithoutPermissions<T> = Omit<T, 'permissions'>;

export enum ProposalExportType {
  OUTLINE = 'OUTLINE',
  NARRATIVE = 'NARRATIVE',
  SPREADSHEET = 'SPREADSHEET'
}

export enum ProposalExportFileType {
  DOCX = 'docx',
  PDF = 'pdf',
  CSV = 'csv',
  ZIP = 'zip'
}
